import { AlertCircle, Check, AlertTriangle } from "lucide-react";
import toast, { ToastType } from "react-hot-toast";
import { FiX } from "react-icons/fi";

type CustomToastOptions = {
  title: string;
  description?: string;
  variant?: ToastType | "warning";
  /** A button next to the message: runs `onClick` and closes the toast. */
  action?: { label: string; onClick: () => void };
  duration?: number;
  /** A toast with the id of one on screen replaces it rather than stacking. */
  id?: string;
};

/**
 * Clear every toast still on screen. The Toaster is mounted app-wide and an
 * error toast lives 5s, so one fired moments before a route change would
 * otherwise follow the user onto the next page (CORE-3036).
 */
export const dismissToasts = () => toast.dismiss();

/** Clear the one toast shown under `id`. */
export const dismissToast = (id: string) => toast.dismiss(id);

export const toasty = ({
  title,
  description,
  variant,
  action,
  duration,
  id,
}: CustomToastOptions) => {
  const variantBorder =
    variant === "success"
      ? "border-toast-success"
      : variant === "error"
        ? "border-status-error-500"
        : variant === "warning"
          ? "border-accent"
          : "border-component-border-color";

  toast.custom(
    (t) => (
      <div
        className={
          `font-neue flex gap-4 rounded-xl border bg-surface-page p-4` +
          ` ${variantBorder} ${t.visible ? "animate-slideUp" : "animate-slideDown"}`
        }
      >
        <div className="flex items-center justify-center">
          {variant === "success" && (
            <div className="rounded-full bg-toast-success p-2">
              <Check size={12} className="text-on-toast-success" />
            </div>
          )}
          {variant === "error" && (
            <div className="rounded-full bg-status-error p-2">
              <AlertCircle size={15} className="text-on-status" />
            </div>
          )}
          {variant === "warning" && (
            <div className="rounded-full bg-status-warning p-2">
              <AlertTriangle size={15} className="text-on-status" />
            </div>
          )}
        </div>
        <div className="flex flex-col items-center justify-center">
          <h3 className="text-text-primary w-full text-sm font-medium">
            {title}
          </h3>
          {description && (
            <p className="text-text-secondary text-xs">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {action && (
            <button
              data-testid="toast-action"
              type="button"
              onClick={() => {
                toast.dismiss(t.id);
                action.onClick();
              }}
              className="whitespace-nowrap rounded-full border border-border bg-surface-card px-3 py-1.5 text-xs font-medium text-ink transition hover:bg-surface-hover focus:outline-none"
            >
              {action.label}
            </button>
          )}
          <button
            data-testid="close-toast"
            type="button"
            onClick={() => toast.dismiss(t.id)}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-surface-card transition hover:bg-surface-hover focus:outline-none"
          >
            <FiX className="h-4 w-4 text-ink" />
          </button>
        </div>
      </div>
    ),
    {
      duration: duration ?? (variant === "error" ? 5000 : 3000),
      ...(id ? { id } : {}),
      style: { zIndex: 9999 },
    },
  );
};
